import { TFunction } from "i18next";
import { CircleAlert, TriangleAlert } from "lucide-react";
import {
  ListItemCardProps,
  ListItemCardMetrics,
  ListItemCardSubtitle,
  ListItemBadge,
} from "@components/ListItemCardCst/types";
import { getActivityUnarchiveInfo } from "@utils/utilsActivityDecompression";
import { CatalogEntityType } from "./types";
import { formatActivityTypeLabel } from "./AddActivity/config/activityTypes";

interface MapOptions {
  t: TFunction;
  tActivity?: TFunction;
  onDetails: (entity: any) => void;
  onPublish?: (entity: any) => void;
  onCreateEdition?: (entity: any) => void;
  omitMetrics?: (keyof ListItemCardMetrics)[];
  hideStandaloneBadge?: boolean;
}

const CLASSROOM_TYPES = ["DA_PHYSICAL_CLASS", "DA_VIRTUAL_CLASS"];

export const isClassroomActivity = (
  entity: any,
  entityType: CatalogEntityType,
): boolean =>
  entityType === "activity" &&
  CLASSROOM_TYPES.includes(entity.type || entity.learning_object_type);

const formatDate = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const buildBadges = (
  entity: any,
  t: TFunction,
  hideStandaloneBadge?: boolean,
): ListItemBadge[] => {
  const badges: ListItemBadge[] = [];

  const unarchive = getActivityUnarchiveInfo(entity);
  if (unarchive) {
    const failed = unarchive.state === "failed";
    badges.push({
      label: t(
        failed ? "badges.unarchive-failed" : "badges.unarchive-loading",
        {
          format: unarchive.format,
          interpolation: { escapeValue: false },
        },
      ),
      variant: failed ? "error" : "warning",
      icon: failed ? <CircleAlert /> : <TriangleAlert />,
      tooltip: failed ? unarchive.errorMessage : undefined,
    });
  }

  if (entity.initiative && entity.initiative.length === 0) {
    badges.push({
      label: t("badges.no-initiative"),
      variant: "warning",
    });
  }

  if (
    CLASSROOM_TYPES.includes(entity.type || entity.learning_object_type) &&
    entity.metadata_counters?.editions === 0
  ) {
    badges.push({
      label: t("badges.no-editions"),
      variant: "warning",
    });
  }

  const publishDate = formatDate(entity.publish_date);
  if (publishDate) {
    const isFuturePublication =
      new Date(entity.publish_date).getTime() > Date.now();
    badges.push({
      label: t(
        isFuturePublication ? "badges.publishing-on" : "badges.published-on",
        {
          date: publishDate,
          interpolation: { escapeValue: false },
        },
      ),
      variant: isFuturePublication ? "info" : "success",
    });
  }

  if (entity.status === "OBSOLETE") {
    badges.push({ label: t("badges.obsolete"), variant: "error" });
  } else if (entity.status === "SUSPENDED") {
    badges.push({ label: t("badges.suspended"), variant: "default" });
  }
  // TODO: da implementare quando avremo le bozze
  // else if (!entity.publish_date) {
  //   badges.push({ label: t("badges.draft"), variant: "default" });
  // }

  if (entity.is_standalone && !hideStandaloneBadge) {
    badges.push({ label: t("badges.standalone"), variant: "info" });
  }

  return badges;
};

const FORMAT_KEY_BY_TYPE: Record<string, string> = {
  SYNC: "sync",
  ASYNC: "async",
  BLENDED: "blended",
};

const formatCombinedTypeLabel = (
  entity: any,
  entityType: "course" | "path",
  t: TFunction,
): string => {
  const raw = String(
    entity.learning_object_type || entity.format || entity.type || "",
  ).toUpperCase();
  const suffix = FORMAT_KEY_BY_TYPE[raw] ?? "default";
  return String(t(`type-labels.${entityType}-${suffix}`));
};

const buildSubtitle = (
  entity: any,
  entityType: CatalogEntityType,
  t: TFunction,
  tActivity?: TFunction,
): ListItemCardSubtitle => {
  const subtitle: ListItemCardSubtitle = {
    id: entity.id,
  };
  if (entityType === "activity") {
    const raw = entity.type || entity.learning_object_type;
    subtitle.activityType = tActivity
      ? formatActivityTypeLabel(raw, tActivity)
      : raw;
  } else if (entityType === "course") {
    subtitle.activityType = formatCombinedTypeLabel(entity, "course", t);
    if (entity.digited_code) subtitle.mode = entity.digited_code;
  } else {
    subtitle.activityType = formatCombinedTypeLabel(entity, "path", t);
  }
  return subtitle;
};

interface MetadataCounters {
  enrolled_users?: number | null;
  enrolled_groups?: number | null;
  initiatives?: number | null;
  courses?: number | null;
  paths?: number | null;
  activities?: number | null;
  editions?: number | null;
}

const pickCounter = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const buildMetrics = (
  entity: any,
  entityType: CatalogEntityType,
): ListItemCardMetrics => {
  const counters: MetadataCounters = entity.metadata_counters ?? {};
  const metrics: ListItemCardMetrics = {};

  const enrolledUsers = pickCounter(counters.enrolled_users);
  if (enrolledUsers !== undefined) metrics.enrolledUsers = enrolledUsers;

  const enrolledGroups = pickCounter(counters.enrolled_groups);
  if (enrolledGroups !== undefined) metrics.enrolledGroups = enrolledGroups;

  const initiatives = pickCounter(counters.initiatives);
  if (initiatives !== undefined) metrics.initiatives = initiatives;

  if (entityType === "activity") {
    const courses = pickCounter(counters.courses);
    if (courses !== undefined) metrics.courses = courses;

    const paths = pickCounter(counters.paths);
    if (paths !== undefined) metrics.paths = paths;
  } else if (entityType === "course") {
    const containedActivities = pickCounter(counters.activities);
    if (containedActivities !== undefined)
      metrics.containedActivities = containedActivities;

    const paths = pickCounter(counters.paths);
    if (paths !== undefined) metrics.paths = paths;
  } else if (entityType === "path") {
    const containedActivities = pickCounter(counters.activities);
    if (containedActivities !== undefined)
      metrics.containedActivities = containedActivities;

    const containedCourses = pickCounter(counters.courses);
    if (containedCourses !== undefined)
      metrics.containedCourses = containedCourses;
  }

  return metrics;
};

export const mapEntityToCard = (
  entity: any,
  entityType: CatalogEntityType,
  options: MapOptions,
): ListItemCardProps => {
  const {
    t,
    tActivity,
    onDetails,
    onPublish,
    onCreateEdition,
    omitMetrics,
    hideStandaloneBadge,
  } = options;
  const isPublished = !!entity.publish_date && entity.status !== "SUSPENDED";

  const metrics = buildMetrics(entity, entityType);
  if (omitMetrics && omitMetrics.length > 0) {
    for (const k of omitMetrics) delete metrics[k];
  }

  return {
    title: entity.title || entity.name || "-",
    subtitle: buildSubtitle(entity, entityType, t, tActivity),
    metrics,
    badges: buildBadges(entity, t, hideStandaloneBadge),
    entity,
    entityType,
    isPublished,
    onPublish: onPublish ? () => onPublish(entity) : undefined,
    onCreateEdition:
      onCreateEdition && isClassroomActivity(entity, entityType)
        ? () => onCreateEdition(entity)
        : undefined,
    actions: [
      {
        label: t("actions.details"),
        onClick: () => onDetails(entity),
      },
    ],
  };
};
